<template>
  <div>
    <div class="d-flex align-items-center flex-wrap mb-2" style="gap: 0.5rem">
      <b-button
        size="sm"
        variant="secondary"
        class="rounded-pill text-nowrap"
        @click="downloadCSV"
      >
        <csv-icon style="font-size: 1.1em; vertical-align: -3%" />
        Baixar CSV
      </b-button>
      <b-button
        size="sm"
        variant="secondary"
        class="rounded-pill text-nowrap"
        @click="downloadXLSX"
      >
        <excel-icon style="font-size: 1.1em; vertical-align: -3%" />
        Baixar XLSX
      </b-button>
      <b-button
        size="sm"
        variant="secondary"
        class="rounded-pill text-nowrap"
        @click="downloadPDF"
      >
        <pdf-icon style="font-size: 1.1em; vertical-align: -3%" />
        Baixar PDF
      </b-button>
    </div>

    <data-table
      ref="dataTable"
      sticky-header
      class="flex-grow-1"
      max-height-table="70vh"
      :left-frozen-columns="leftFrozenColumns"
      :static-columns="staticColumns"
      :table-data="tableData"
      :file-prefix="`Manejo diário`"
      />

  </div>
</template>

<script>
import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import DataTable from "@/pages/components/shared/table/DataTable.vue";
import table from "@/mixins/table";
import { calcPercentageRounded } from "@/pages/components/helpers";

pdfMake.vfs = pdfFonts.pdfMake.vfs;


export default {
  name: "report-management-daily",
  mixins: [table],
  components: {
    DataTable,
  },
  props: {
    tableData: {
      type: Array,
      required: true
    },
    // Data do relatório (dia buscado). O PDF usa esta data, não a de hoje.
    reportDate: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      leftFrozenColumns: [
        {
          title: "Nome do setor",
          field: "SECTOR_NAME",
          minWidth: 130,
          maxWidth: 130
        },
      ],
      staticColumns: [
        {
          title: "NDC",
          field: "NDC",
          minWidth: 90,
          width: 90
        },
        {
          title: "Cultivar",
          field: "FARM_NAME",
          minWidth: 200,
          maxWidth: 200,
          formatter: (cell) => {
            const cellValue = cell.getValue();
            const maxLength = 20;
            this.decimalFormatter(cell.getValue());
            
            if(cellValue.length > maxLength) {
              return `${cellValue.slice(0,maxLength)}...`
            }

            return cellValue;
          },
        },
        {
          title: "Armazenamento inicial (mm)",
          field: "INITIAL_STORAGE",
          minWidth: 150,
          maxWidth: 150,
          accessorDownload: (val) => this.decimalFormatter(val),
        },
        {
          title: "Armazenamento final (mm)",
          field: "FINAL_STORAGE",
          minWidth: 150,
          maxWidth: 150,
          accessorDownload: (val) => this.decimalFormatter(val),
        },
        {
          title: "Disponibilidade de água (%)",
          field: "WATER_DISPONIBILITY",
          minWidth: 200,
          maxWidth: 300,
          tooltip: cell => {
            return (
              `Água disponível: ${this.calcPercentageRounded(cell.getData().FINAL_STORAGE, cell.getData().MAX_STORAGE)}% (${cell.getData().FINAL_STORAGE}mm)`+
              `\npara armazenamento máximo de: ${cell.getData().MAX_STORAGE}mm`
            )
          },
          formatter:(cell) => {
            const storagePercentage = this.calcPercentageRounded(cell.getData().FINAL_STORAGE, cell.getData().MAX_STORAGE);
            const availability = (1 - cell.getData().AVAILABILITY) * 100;

            let bgColor = 'bg-';

            if(storagePercentage <= availability) {
              bgColor += 'danger';
            } else if(storagePercentage <= ((100 - availability)/2 + availability)) {
              bgColor += 'warning'
            } else {
              bgColor += '3v3';
            }

            return `
              <div
                data-max="100"
                data-min="0"
                class="${bgColor}"
                style="display: inline-block;
                position: relative;
                width: ${storagePercentage}%;
                height: 100%;" >
              </div>`
                // return "<span style='color:#3FB449; font-weighst:bold;'>" + value + "</span>";
          },
          accessorDownload: (val, rowData) => {
            return this.calcPercentageRounded(rowData.FINAL_STORAGE, rowData.MAX_STORAGE)
          },
          hozAlign: "start",
        },
        {
          title: "Excedente previsto (mm)",
          field: "EXCESS",
          formatter: (cell) => this.decimalFormatter(cell.getValue()),
        },
        {
          title: "Agenda aplicada",
          field: "IRRIGATED_BLADE_INFOS",
          minWidth: 250,
          // maxWidth: 250
        },
        {
          title: "Agenda sugerida",
          field: "BLADE_SUGGESTION_INFOS",
          minWidth: 250,
          // maxWidth: 250
        },
        {
          title: "Usuário",
          field: "EDITOR_NAME",
          minWidth: 250,
          formatter: (cell) => {
            const editor = cell.getValue();

            const fieldVal = editor ? editor : "-";

            return `
              <span
                class="${editor? "font-weight-bold" : ""}"
              >
                ${fieldVal}
              </span>
            `;
          },
          accessorDownload: (val, row) => {
            const editorNameFieldVal = !!row.EDITOR_NAME;
            return editorNameFieldVal ? val : '-'
          },
          // maxWidth: 250
        },
        // {
        //   title: "Profundidade de raiz (cm)",
        //   field: "DEPTH_ROOT"
        // },
        // {
        //   title: "Projeção de rama (m)",
        //   field: "BRANCH"
        // },
      ],
    }
  },
  methods: {
    calcPercentageRounded,
    downloadCSV() {
      return this.$refs.dataTable.downloadCSV();
    },
    downloadXLSX() {
      return this.$refs.dataTable.downloadXLSX();
    },
    // PDF próprio (pdfMake) no mesmo padrão do histórico de manejo (SectorDetails), em vez do PDF
    // nativo do Tabulator — mesma estética: paisagem, cabeçalho verde, zebra, fonte 8.
    downloadPDF() {
      const data =
        this.$refs.dataTable?.tabulator?.getData() ?? this.tableData;
      if (!data.length) return;

      const fmt = (val) =>
        typeof val === "number" ? val.toFixed(2) : val ?? "-";

      // Disponibilidade de água: mesma lógica de cor da barra de armazenamento na tela do relatório.
      const stateColor = (percentage, row) => {
        const availability = (1 - Number(row.AVAILABILITY)) * 100;
        if (percentage <= availability) return "#dc3545";
        if (percentage <= (100 - availability) / 2 + availability) return "#d39e00";
        return "#3d9161";
      };

      const cell = (text, extra = {}) => ({
        text: String(text),
        style: "tableData",
        ...extra,
      });

      const rows = data.map((row) => {
        const availability = this.calcPercentageRounded(
          row.FINAL_STORAGE,
          row.MAX_STORAGE,
        );
        return [
        cell(row.SECTOR_NAME),
        cell(row.NDC == null ? "-" : Math.round(row.NDC)),
        cell(row.FARM_NAME ?? "-"),
        cell(fmt(row.INITIAL_STORAGE)),
        cell(fmt(row.FINAL_STORAGE)),
        cell(`${availability}%`, { color: stateColor(availability, row), bold: true }),
        cell(fmt(row.EXCESS), Number(row.EXCESS) > 0 ? { color: "#dc3545", bold: true } : {}),
        cell(row.IRRIGATED_BLADE_INFOS),
        cell(row.BLADE_SUGGESTION_INFOS),
        cell(row.EDITOR_NAME ?? "-"),
      ];
      });

      const headerStyle = {
        style: "tableHeader",
        fillColor: "#7bb34b",
        color: "#fff",
      };
      const headers = [
        "Setor",
        "NDC",
        "Cultivar",
        "Arm. Inicial (mm)",
        "Arm. Final (mm)",
        "Disp. de Água (%)",
        "Excedente (mm)",
        "Agenda Aplicada",
        "Agenda Sugerida",
        "Usuário",
      ].map((h) => ({ text: h, ...headerStyle }));

      const reportDay = this.reportDate ? moment(this.reportDate) : moment();

      const content = [
        { text: "Manejo Diário", style: "title" },
        { text: reportDay.format("DD/MM/YYYY"), style: "date", alignment: "right" },
        {
          table: {
            headerRows: 1,
            widths: [
              "auto", "auto", "auto", "auto", "auto",
              "auto", "auto", "*", "*", "auto",
            ],
            body: [headers, ...rows],
          },
          layout: {
            fillColor: (rowIndex) =>
              rowIndex > 0 && rowIndex % 2 === 0 ? "#f0f4f4" : null,
          },
        },
      ];

      pdfMake
        .createPdf({
          pageOrientation: "landscape",
          pageSize: "A4",
          pageMargins: [20, 30, 20, 30],
          content,
          styles: {
            title: { fontSize: 12, bold: true, margin: [0, 0, 0, 4] },
            date: { fontSize: 9, margin: [0, 0, 0, 8] },
            tableHeader: { fontSize: 8, bold: true, alignment: "center" },
            tableData: { fontSize: 8, alignment: "center" },
          },
        })
        .download(`Manejo diário ${reportDay.format("YYYY-MM-DD")}.pdf`);
    },
  },
}
</script>
